const { debounce } = window.BAO.Utils

class CartItem extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['remove']
  }

  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.removeLineItem = this.removeLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.debouncedOnQuantityChange = debounce((event) => {
      this.onQuantityChange(event)
    }, 500)

    if (this.els?.remove.exists) {
      this.listeners.add(this.els.remove.element, 'click', this.removeLineItem)
    }

    if (this.els?.quantity?.exists) {
      this.handleQuantityChange = this.handleQuantityChange.bind(this)
      this.listeners.add(
        this.els.quantity.element,
        'change',
        this.handleQuantityChange,
      )
    }

    if (this.els?.variant?.exists) {
      this.handleVariantChange = this.handleVariantChange.bind(this)
      this.listeners.add(
        this.els.variant.element,
        'change',
        this.handleVariantChange,
      )
    }

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  async removeLineItem (e) {
    e.preventDefault()

    this.loading = true

    const state = await window.BAO.Cart.removeItem(this.dataset.key, {
      sections: this.mappedSections,
    })

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)
  }

  handleQuantityChange (e) {
    const newValue = parseInt(e.target.value, 10)

    this.onQuantityChange({ detail: { value: newValue } })
  }

  async onQuantityChange (e) {
    this.loading = true

    const state = await window.BAO.Cart.updateQuantity(
      this.dataset.key,
      e.detail.value,
      { sections: this.mappedSections },
    )

    this.loading = false

    if (!state) {
      this.handleCartErrorRender()

      return
    }

    if ('sections' in state) this.renderSections(state.sections)
  }

  async handleVariantChange (e) {
    this.loading = true

    try {
      const variantSelect = e.target

      const maxQuantity =
        variantSelect.options[variantSelect.selectedIndex]?.dataset
          .maxInventory ?? 1
      const currentQuantity = variantSelect.dataset.currentQuantity

      const newQuantity = Math.min(currentQuantity, maxQuantity)

      const newVariant = variantSelect.value

      // Add the new item and don't request sections
      const state = await window.BAO.Cart.addItem(
        { id: newVariant, quantity: newQuantity },
        { sections: [] },
      )

      if (!state) {
        this.handleCartErrorRender()

        return
      }

      // Remove the old item
      const removeState = await window.BAO.Cart.removeItem(this.dataset.key, {
        sections: this.mappedSections,
      })

      // Render sections
      if ('sections' in removeState) this.renderSections(removeState.sections)
    } catch (error) {
      console.error('Failed to update line item:', error)
    } finally {
      this.loading = false
    }
  }

  get mappedSections () {
    return this.sections.map((s) => s.section)
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.inPushCart) {
      this.pushCart.loading = val
    }

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
